import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { setConfig, setSelectedLanguage, setLoading } from '../../../store/slices/appSlice';
import { useAppConfig } from '../../../hooks/useAppConfig';
import { useVisitorData } from '../../../hooks/useVisitorData';
import { touchlessService } from '../../../services/api/touchlessService';
import { SUPPORTED_LANGUAGES } from '../../../utils/constants';
import './LanguageDropdown.scss';

const hexToHsl = (hex: string): { h: number; s: number; l: number } | null => {
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const c = m[1];
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
};

const lightenColor = (hex: string, amount: number): string => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  const l = Math.min(100, Math.max(0, hsl.l + amount));
  return `hsl(${hsl.h.toFixed(0)}, ${hsl.s.toFixed(0)}%, ${l.toFixed(0)}%)`;
};

const LanguageDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const { config, themeColors } = useAppConfig();
  const { visitorTypeId, refreshLinkedValuesFromConfig } = useVisitorData();
  const currentLang = useSelector((state: RootState) => state.app.selectedLanguage ?? 'en');
  const primaryLanguage = useSelector((state: RootState) => state.app.primaryLanguage);
  const authFlow = useSelector((state: RootState) => state.auth.authFlow);
  const additionalLanguages = config?.additionalLanguages ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const rawOptions = [primaryLanguage, ...(additionalLanguages ?? [])].filter(
    (code): code is string => Boolean(code),
  );
  const seen = new Set<string>();
  const options = rawOptions.filter((code) => {
    if (seen.has(code)) return false;
    seen.add(code);
    return true;
  });

  // Only show the chooser if the org configured more than one language.
  if (options.length < 2) return null;

  const handleSelect = async (code: string) => {
    setIsOpen(false);
    if (code === currentLang) return;
    dispatch(setLoading(true));
    try {
      const fetchConfig =
        authFlow === 'visitor'
          ? touchlessService.getVisitorOrgConfig.bind(touchlessService)
          : touchlessService.getOrgConfig.bind(touchlessService);
      const translatedConfig = await fetchConfig(code);
      dispatch(setConfig(translatedConfig));
      dispatch(setSelectedLanguage(code));
      refreshLinkedValuesFromConfig(translatedConfig, visitorTypeId);
    } catch (e) {
      void e;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const activeGradient = `linear-gradient(135deg, ${themeColors.btnColor}, ${lightenColor(themeColors.btnColor, 15)})`;

  return (
    <div className="language-dropdown" ref={wrapperRef}>
      <button
        className={`lang-trigger${isOpen ? ' lang-trigger--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        style={{ color: themeColors.btnColor }}
      >
        <span className="lang-trigger__code">{currentLang.toUpperCase()}</span>
        <i className={`bi bi-chevron-down lang-chevron${isOpen ? ' rotated' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="lang-panel" role="listbox" aria-label="Select language">
          <div className="lang-panel__header">
            <i className="bi bi-globe2"></i>
            <span>Select Language</span>
          </div>
          <div className="lang-panel__list">
            {options.map((code) => {
              const info = SUPPORTED_LANGUAGES[code as keyof typeof SUPPORTED_LANGUAGES];
              const native = info?.native ?? code.toUpperCase();
              const english = info?.english ?? code.toUpperCase();
              const active = code === currentLang;
              return (
                <div
                  key={code}
                  className={`lang-option${active ? ' lang-option--active' : ''}`}
                  role="option"
                  aria-selected={active}
                  onClick={() => handleSelect(code)}
                  style={active ? { background: activeGradient } : undefined}
                >
                  <span className="lang-option__tile">{code.toUpperCase()}</span>
                  <div className="lang-option__names">
                    <span className="lang-option__native">{native}</span>
                    <span className="lang-option__english">{english}</span>
                  </div>
                  {active && <i className="bi bi-check2 lang-option__check"></i>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
